import React from 'react';
import Banner from '../Components/Banner';
import { Link } from 'react-router-dom';

const Section = ({ icon, title, children }) => (
    <div className="service-card-v2 tmponhover mb-10">
        <h2 className="service-card-num">
            <span>{icon}</span>{title}
        </h2>
        <div className="service-para mt-4">
            {children}
        </div>
    </div>
);

const ReturnPolicy = () => {
    return (
        <>
            <Banner heading="Return Policy" text="Return Policy" />
            <div className="tmp-section-gap">
                <div className="container">

                    {/* Header */}
                    <div className="text-center mb-14 section-head">
                        <div className="section-sub-title center-title">
                            <span className="subtitle">Transparency First</span>
                        </div>
                        <h2 className="title split-collab mt-2">Our <span style={{ color: '#e74c3c' }}>Return & Refund</span> Policy</h2>
                        <p className="service-para mt-4 max-w-2xl mx-auto">
                            At HNH Soft Tech Solutions, we are committed to delivering exceptional quality. This policy applies to all clients globally — including the United States, United Kingdom, and Pakistan.
                        </p>
                        <p className="service-para mt-2 text-sm">
                            Last updated: March 2025
                        </p>
                    </div>

                    <Section icon="⏱️" title="Cancellation Window">
                        <p className="mb-3">
                            You may cancel your project and request a full refund within <strong>48 hours</strong> of making the initial deposit, provided that no work has commenced on your project.
                        </p>
                        <p>
                            To initiate cancellation, please contact us via email at <a href="mailto:info@hnhsofttechsolutions.com" className="text-[#e74c3c]">info@hnhsofttechsolutions.com</a> or call your regional contact number.
                        </p>
                    </Section>

                    <Section icon="📋" title="Eligibility for Refund">
                        <ul className="space-y-3 list-none">
                            {[
                                { eligible: true, text: "Cancellation requested within 48 hours of deposit — before any work has begun" },
                                { eligible: true, text: "Project delivery was significantly delayed beyond the agreed timeline due to our team's fault" },
                                { eligible: true, text: "Delivered work fundamentally does not match the agreed scope of work (with documented evidence)" },
                                { eligible: false, text: "Client-side delays or changes in project scope after development commenced" },
                                { eligible: false, text: "Cancellation after 50% or more of the project has been completed" },
                                { eligible: false, text: "Dissatisfaction due to subjective design preferences that differ from the approved mockups" },
                            ].map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${item.eligible ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {item.eligible ? '✓' : '✗'}
                                    </span>
                                    <span>{item.text}</span>
                                </li>
                            ))}
                        </ul>
                    </Section>

                    <Section icon="💰" title="Partial Refund Schedule">
                        <p className="mb-4">If work has already begun, refunds are calculated based on work completed:</p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-base">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="title text-left py-4 pr-4 text-lg">Stage of Completion</th>
                                        <th className="title text-left py-4 pr-4 text-lg">Refund Percentage</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {[
                                        ["0% – no work started (within 48 hrs)", "100% refund"],
                                        ["Less than 25% complete", "75% refund"],
                                        ["25%–50% complete", "50% refund"],
                                        ["51%–75% complete", "25% refund"],
                                        ["More than 75% or project delivered", "No refund"],
                                    ].map(([stage, refund], i) => (
                                        <tr key={i}>
                                            <td className="py-4 pr-4 service-para mt-0">{stage}</td>
                                            <td className="py-4 service-para mt-0 font-medium" style={{ color: refund === "100% refund" ? '#27ae60' : refund === "No refund" ? '#e74c3c' : '#f39c12' }}>{refund}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    <Section icon="🌍" title="Regional Payment & Refund Notes">
                        <div className="grid md:grid-cols-3 gap-6">
                            {[
                                {
                                    flag: "🇺🇸", country: "United States",
                                    details: "Refunds processed via PayPal, bank transfer, or Wise within 5–7 business days."
                                },
                                {
                                    flag: "🇬🇧", country: "United Kingdom",
                                    details: "Refunds processed via bank transfer or Wise. Typically 3–5 business days."
                                },
                                {
                                    flag: "🇵🇰", country: "Pakistan",
                                    details: "Refunds processed via local bank transfer (HBL, UBL,bank Alfalah, MCB, Meezan, etc.) within 3–5 business days."
                                },
                            ].map((region, i) => (
                                <div key={i} className="service-card-v2 tmponhover text-center p-6">
                                    <div className="text-3xl mb-2">{region.flag}</div>
                                    <h4 className="service-card-num mb-2">{region.country}</h4>
                                    <p className="service-para text-sm">{region.details}</p>
                                </div>
                            ))}
                        </div>
                    </Section>

                    <Section icon="📞" title="How to Request a Refund">
                        <ol className="space-y-3 list-none counter-reset-none">
                            {[
                                "Email us at info@hnhsofttechsolutions.com with subject line: 'Refund Request – [Your Project Name]'",
                                "Include your project details, invoice number, and reason for the refund request",
                                "Our team will review your request within 2 business days",
                                "You will receive a decision confirmation via email",
                                "Approved refunds are processed within 5–7 business days depending on your region",
                            ].map((step, i) => (
                                <li key={i} className="flex items-start gap-4">
                                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#e74c3c] text-white text-xs font-bold flex items-center justify-center mt-0.5">
                                        {i + 1}
                                    </span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                    </Section>

                    {/* Contact CTA */}
                    <div className="service-card-v2 text-center py-10 px-6">
                        <h3 className="service-card-num mb-3">Need Help?</h3>
                        <p className="service-para mb-6">
                            If you have questions about this policy, our team is here to help.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <a href="mailto:info@hnhsofttechsolutions.com" className="tmp-btn btn-primary">Email Us</a>
                            <Link to="/contact" className="tmp-btn border border-gray-300 bg-transparent text-gray-800 hover:bg-gray-100">Contact Page</Link>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default ReturnPolicy;
